import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import {
    VersionsPageContainer,
    ContentWrapper,
    HeaderContainer,
    Title,
    ButtonsContainer,
    IconsContainer,
    DocumentFeatureButton,
    DocumentFeatureGlyph,
} from "./DocumentVersionsPage.styles";
import Sidebar from "../../components/Sidebar";
import IconButton from "../../components/Buttons/IconButton";
import Button from "../../components/Buttons/Button";
import {
  practicesIcon,
  modulesIcon,
  studentForDocIcon,
  testsIcon,
} from "assets/icons";
import { ConfirmModal } from "../../components/Modals";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { DocumentVersion, DocumentTemplate } from "../../services/documentService/documentService.types";
import { createDocumentVersion, deleteDocumentVersion, downloadGeneratedDocument, fetchDocumentTemplate, fetchDocumentVersions } from "../../services/documentService/documentService";
import { ButtonWrapper } from "../TemplatesPage/TemplatesPage.styles";
import Table from "../../components/Table/Table";
import { getTemplateSchemaValues, updateTemplateSchemaValue } from "../../services/templateSchemaService/templateSchemaService";
import DocumentModal from "../../components/Modals/DocumentModal/DocumentModal";
import { Field } from "../../components/Modals/DocumentModal/DocumentModal.types";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";
import { Column } from "./DocumentVersionsPage.types";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, docxIcon, editIcon, pdfIcon } from "assets/icons";
const DocumentVersionsPage: React.FC = () => {
    const navigate = useNavigate();
    const { templateId } = useParams<{ templateId: string }>();
    const [searchParams] = useSearchParams();
    const templateName = searchParams.get("templateName") || "Неизвестный шаблон";

    const {
        items: versions,
        setItems: setVersions,
        loading,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalPages,
        handleSort,
        reload: loadVersions,
    } = useListPage<DocumentVersion, Awaited<ReturnType<typeof fetchDocumentVersions>>>({
        fetcher: ({ page, pageSize: size, orderBy }) =>
            fetchDocumentVersions(Number(templateId), page, size, orderBy),
        select: (response) => ({ items: response.results, total: response.count }),
        errorMessage: "Ошибка загрузки документов",
        enabled: Boolean(templateId),
    });

    const [currentTemplate, setCurrentTemplate] = useState<DocumentTemplate | null>(null);
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
    const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0);
    const [collectedData, setCollectedData] = useState<Record<string, string>>({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [versionToDelete, setVersionToDelete] = useState<DocumentVersion | null>(null);
    const [editingVersion, setEditingVersion] = useState<DocumentVersion | null>(null);
    const [versionId, setVersionId] = useState<number | null>(null);
    const [modalInitialValues, setModalInitialValues] = useState<Record<string, string>>({});
    // Флаг для этапа ввода названия документа
    const [isNameStep, setIsNameStep] = useState<boolean>(false);

    // Состояния для отложенного удаления и уведомлений
    const [pendingDelete, setPendingDelete] = useState<number | null>(null);
    const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
    const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
    const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
    const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const generatingToastTimerRef = useRef<NodeJS.Timeout | null>(null);
    const generatingToastVisibleRef = useRef(false);

    const toast = useNotify();

    // Пагинация и сортировка

    // Проверка на подписку
    const isSubscribed = currentTemplate?.is_subscribed ?? false;

    useEffect(() => {
        if (!templateId) {
            toast.show({
                severity: "error",
                summary: "Ошибка",
                detail: "Необходимо указать документ.",
            });
            navigate("/login");
            return;
        }
        loadTemplate();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId]);


    const loadVersionData = async (versionId: number) => {
        if (!currentTemplate) return {};
        try {
            const schemaValues = await getTemplateSchemaValues(versionId);
            return schemaValues || {};
        } catch (error) {
            logger.error("Ошибка при загрузке данных документа:", error);
            return {};
        }
    };

    const openEditVersionModal = async (version: DocumentVersion) => {
        setCurrentGroupIndex(0);
        setEditingVersion(version);
        setVersionId(version.id);
        const savedData = await loadVersionData(version.id);
        const cleanedData: Record<string, string> = Object.fromEntries(
            Object.entries(savedData).map(([key, value]) => [key, value ?? ""])
        );
        setModalInitialValues(cleanedData);
        setCollectedData(cleanedData);
        setIsDocumentModalOpen(true);
    };

    const loadTemplate = async () => {
        if (!templateId) return;
        try {
            const template = await fetchDocumentTemplate(Number(templateId));
            setCurrentTemplate(template);
        } catch (error) {
            logger.error("Ошибка при загрузке шаблона:", error);
        }
    };

    // Этап создания нового документа: сначала ввод названия
    const openCreateVersionModal = () => {
        if (!currentTemplate) return;
        setIsNameStep(true);
        setCurrentGroupIndex(0);
        setCollectedData({});
        setEditingVersion(null);
        setVersionId(null);
        setModalInitialValues({});
        setIsDocumentModalOpen(true);
    };

    const handleModalClose = () => {
        setIsDocumentModalOpen(false);
        setIsNameStep(false);
        setEditingVersion(null);
        setModalInitialValues({});
    };


    const handleModalSubmit = async (formData: Record<string, string>) => {
        logger.log("Форма отправлена:", formData);
        const newCollectedData = {
            ...collectedData,
            ...formData,
        };
        setCollectedData(newCollectedData);

        // Если на этапе ввода названия, создаём новый документ
        if (isNameStep) {
            try {
                const response = await createDocumentVersion(Number(templateId), newCollectedData.name);
                setVersionId(response.id);
                setVersions([...versions, response]);
                setRecentlyCreated(response.id);
                toast.show({
                    severity: "success",
                    summary: "Создано",
                    detail: "Документ создан",
                    life: 3000,
                });
                // Убираем уведомление через 3 секунды
                setTimeout(() => setRecentlyCreated(null), 3000);
            } catch (error) {
                logger.error("Ошибка при создании документа:", error);
                return;
            }
            setIsNameStep(false);
            return;
        }

        // Для последующих этапов сохраняем данные через updateTemplateSchemaValue
        try {
            await updateTemplateSchemaValue(
                versionId !== undefined ? versionId : editingVersion?.id || null,
                Number(templateId),
                newCollectedData
            );
            if (editingVersion) {
                setRecentlyEdited(editingVersion.id);
                setTimeout(() => setRecentlyEdited(null), 3000);
            }
        } catch (error) {
            logger.error("Ошибка при сохранении данных:", error);
        }

        if (currentTemplate && currentGroupIndex < currentTemplate.question_groups.length - 1) {
            setCurrentGroupIndex(currentGroupIndex + 1);
        } else {
            toast.show({
                severity: "success",
                summary: "Сохранено",
                detail: "Данные сохранены",
                life: 3000,
            });
            setIsDocumentModalOpen(false);
        }
    };


    const openDeleteModal = (version: DocumentVersion) => {
        setVersionToDelete(version);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setVersionToDelete(null);
    };

    const confirmDelete = () => {
        if (versionToDelete) {
            handleDeleteVersion(versionToDelete.id);
            closeDeleteModal();
        }
    };

    const handleDeleteVersion = (id: number) => {
        setPendingDelete(id);
        deleteTimeoutRef.current = setTimeout(async () => {
            try {
                await deleteDocumentVersion(id);
                setPendingDelete(null);
                toast.show({
                    severity: "success",
                    summary: "Удалено",
                    detail: "Документ успешно удалён",
                    life: 2000,
                });
                loadVersions();
            } catch (error) {
                toast.show({
                    severity: "error",
                    summary: "Ошибка",
                    detail: "Не удалось удалить документ",
                    life: 3000,
                });
                logger.error("Ошибка при удалении документа:", error);
                setPendingDelete(null);
            }
        }, 5000);
    };

    const restoreVersionHandler = () => {
        if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
            deleteTimeoutRef.current = null;
        }
        const restoredId = pendingDelete;
        setPendingDelete(null);
        setRecentlyRestored(restoredId);
        toast.show({
            severity: "success",
            summary: "Восстановлено",
            detail: "Удаление документа отменено",
            life: 3000,
        });
        setTimeout(() => setRecentlyRestored(null), 3000);
    };

    // Функции для обработки кликов по иконкам
    const handleModulesClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, version: DocumentVersion) => {
        e.stopPropagation();
        navigate(`/versions/${version.id}/modules?documentName=${version.name}&templateId=${templateId}&templateName=${templateName}`);
    };

    const handleTestsClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, version: DocumentVersion) => {
        e.stopPropagation();
        navigate(`/versions/${version.id}/tests?documentName=${version.name}&templateId=${templateId}&templateName=${templateName}`);
    };

    const handlePracticesClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, version: DocumentVersion) => {
        e.stopPropagation();
        navigate(`/versions/${version.id}/practices?documentName=${version.name}&templateId=${templateId}&templateName=${templateName}`);
    };

    const handlesStudentsClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, version: DocumentVersion) => {
        e.stopPropagation();
        navigate(`/versions/${version.id}/students?documentName=${version.name}&templateId=${templateId}&templateName=${templateName}`);
    };

    const clearGeneratingToastTimer = () => {
        if (generatingToastTimerRef.current) {
            clearTimeout(generatingToastTimerRef.current);
            generatingToastTimerRef.current = null;
        }
    };

    const showGeneratingToast = () => {
        clearGeneratingToastTimer();
        generatingToastTimerRef.current = setTimeout(() => {
            toast.clear();
            toast.show({
                severity: "info",
                summary: "Готовим файл",
                detail: "Подождите, идёт формирование документа",
                sticky: true,
            });
            generatingToastVisibleRef.current = true;
            generatingToastTimerRef.current = null;
        }, 400);
    };

    const hideGeneratingToast = async () => {
        clearGeneratingToastTimer();

        if (!generatingToastVisibleRef.current) {
            return;
        }

        generatingToastVisibleRef.current = false;
        toast.clear();

        // PrimeReact держит toast в DOM во время exit-анимации (~300 мс).
        // Не показываем следующее сообщение, пока старое визуально не исчезло.
        await new Promise((resolve) => setTimeout(resolve, 350));
    };

    const handleDownloadError = async (error: any) => {
        await hideGeneratingToast();

        let errorMessage = "Ошибка при скачивании документа";
        if (error.response && error.response.data) {
            try {
                // Преобразуем Blob в текст
                const errorText = await error.response.data.text();
                // Парсим текст как JSON
                const errorJson = JSON.parse(errorText);
                if (errorJson.error && errorJson.missing_fields) {
                    errorMessage = errorJson.error + "\n" + errorJson.missing_fields.join("\n");
                } else {
                    errorMessage = errorJson.error || errorMessage;
                }
            } catch (parseError) {
                // Если парсинг не удался, оставляем сообщение по умолчанию
            }
        }

        toast.show({
            severity: "warn",
            summary: "Ошибка",
            detail: errorMessage,
            life: 10000,
        });
    };

    const handleDownloadDocx = async (versionId: number, versionName: string) => {
        showGeneratingToast();
        try {
            await downloadGeneratedDocument(versionId, versionName, "docx");
            await hideGeneratingToast();
        } catch (error) {
            await handleDownloadError(error);
        }
    };

    const handleDownloadPdf = async (versionId: number, versionName: string) => {
        showGeneratingToast();
        try {
            await downloadGeneratedDocument(versionId, versionName, "pdf");
            await hideGeneratingToast();
        } catch (error) {
            await handleDownloadError(error);
        }
    };




    // Формирование строк таблицы с логикой DeletedRow и RestoredRow, а также отображением иконок по флагам
    const rows: any[] = [];
    versions.forEach((version) => {
        if (version.id === pendingDelete) {
            rows.push({
                name: (
                    <DeletedRow
                        id={version.id}
                        onRestore={restoreVersionHandler}
                        colSpan={5}
                        message={`Документ "${version.name}" будет удалён через 3 секунды.`}
                        targetText="Отменить удаление"
                    />
                ),
            });
        } else {
            rows.push({
                name: version.name,
                created_at: new Date(version.created_at).toLocaleDateString(),
                updated_at: new Date(version.updated_at).toLocaleDateString(),
                icons: (
                    <IconsContainer>
                        {version.with_modules && (
                            <DocumentFeatureButton
                                type="button"
                                title="Модули"
                                aria-label="Модули"
                                onClick={(e) => handleModulesClick(e, version)}
                                >
                                  <DocumentFeatureGlyph as={modulesIcon} aria-hidden="true" />
                            </DocumentFeatureButton>
                        )}
                        {version.with_tests && (
                            <DocumentFeatureButton
                                type="button"
                                title="Тесты"
                                aria-label="Тесты"
                                onClick={(e) => handleTestsClick(e, version)}
                                >
                                  <DocumentFeatureGlyph as={testsIcon} aria-hidden="true" />
                            </DocumentFeatureButton>
                        )}
                        {version.with_practices && (
                            <DocumentFeatureButton
                                type="button"
                                title="Практические"
                                aria-label="Практические"
                                onClick={(e) => handlePracticesClick(e, version)}
                                >
                                  <DocumentFeatureGlyph as={practicesIcon} aria-hidden="true" />
                            </DocumentFeatureButton>
                        )}
                        {version.with_students && (
                            <DocumentFeatureButton
                                type="button"
                                title="Студенты"
                                aria-label="Студенты"
                                onClick={(e) => handlesStudentsClick(e, version)}
                                >
                                  <DocumentFeatureGlyph as={studentForDocIcon} aria-hidden="true" />
                            </DocumentFeatureButton>
                        )}
                    </IconsContainer>
                ),
                buttons: (
                    <ButtonsContainer>
                        {isSubscribed && (
                            <IconButton
                                icon={editIcon}
                                altText="Редактировать"
                                onClick={() => openEditVersionModal(version)}
                            />
                        )}
                        <IconButton
                            icon={docxIcon}
                            altText="Скачать DOCX"
                            onClick={() => handleDownloadDocx(version.id, version.name)}
                        />
                        <IconButton
                            icon={pdfIcon}
                            altText="Скачать PDF"
                            onClick={() => handleDownloadPdf(version.id, version.name)}
                        />
                        {isSubscribed && (
                        <IconButton
                            icon={delIcon}
                            tone="danger"
                            altText="Удалить"
                            onClick={() => openDeleteModal(version)}
                        />
                        )}
                    </ButtonsContainer>
                ),
            });
            if (recentlyRestored === version.id) {
                rows.push({
                    name: (
                        <RestoredRow colSpan={5} message="Документ успешно восстановлён" />
                    ),
                });
            }
            if (recentlyCreated === version.id) {
                rows.push({
                    name: (
                        <RestoredRow colSpan={5} message="Документ успешно создан" />
                    ),
                });
            }
            if (recentlyEdited === version.id) {
                rows.push({
                    name: (
                        <RestoredRow colSpan={5} message="Документ успешно отредактирован" />
                    ),
                });
            }
        }
    });

    // Определение полей модального окна
    const modalFields = useMemo(() => {
        if (!currentTemplate) return [];
        if (isNameStep) {
            return [{
                id: 0,
                name: "name",
                placeholder: "Введите название документа",
                required: true,
                field_type: "text",
            }];
        } else {
            /*
              Группы может не быть: у шаблона без вопросов массив пуст,
              и обращение по индексу давало undefined. Дальше шло чтение
              .schemas — страница падала с «Cannot read properties
              of undefined» ещё до отрисовки.
            */
            const group = currentTemplate.question_groups?.[currentGroupIndex];

            if (!group) {
                return [];
            }

            return group.schemas.map(schema => ({
                id: Number(schema.id),
                name: schema.key,
                placeholder: schema.question || "",
                required: schema.required,
                options: schema.answer_options.map(option => option.value),
                field_type: schema.field_type,
            }));
        }
    }, [currentTemplate, isNameStep, currentGroupIndex]);

    const submitButtonText = useMemo(() => {
        if (isNameStep) return "Создать";
        if (currentTemplate) {
            return currentGroupIndex === currentTemplate.question_groups.length - 1 ? "Сохранить" : "Подтвердить";
        }
        return "";
    }, [isNameStep, currentTemplate, currentGroupIndex]);

    const headers: Column[] = [
        { label: "Название документа", key: "name", fieldName: "name" },
        { label: "Дата создания", key: "created_at", fieldName: "created_at" },
        { label: "Дата обновления", key: "updated_at", fieldName: "updated_at" },
    ]

    if (isSubscribed) {
        headers.push({ label: "Дополнительно", key: "icons", fieldName: "icons", sortable: false });
    }
    headers.push({ label: "", key: "buttons", sortable: false, width: "10px" })


    return (
        <VersionsPageContainer>
            <Sidebar />
            <ContentWrapper>
                <Breadcrumbs
                  items={[
                    { label: "Шаблоны документов", to: "/documents" },
                    { label: templateName },
                  ]}
                />
                <HeaderContainer>
                    <Title>{templateName}</Title>
                    <ButtonWrapper>
                        {currentTemplate?.external_link && (
                            <Button onClick={() => window.open(currentTemplate.external_link, '_blank')} variant="accent" size="lg">
                                Смотреть инструкцию
                            </Button>
                        )}
                        {isSubscribed && (
                            <Button onClick={openCreateVersionModal} variant="primary" size="lg">
                                Добавить документ
                            </Button>
                        )}
                    </ButtonWrapper>
                </HeaderContainer>
                <Table
                  emptyState={{
                    title: "Документов пока нет",
                    description:
                      "Добавьте документ — здесь будут храниться все его версии.",
                  }}
                    rowAmountOptions={[
                        { label: "7 записей", value: 7 },
                        { label: "15 записей", value: 15 },
                        { label: "20 записей", value: 20 },
                    ]}
                    headers={headers}
                    rows={rows}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    loading={loading}
                    minHeight="200px"
                    specialRowKey="name"
                    onSort={handleSort}
                />
            </ContentWrapper>
            {isDocumentModalOpen && currentTemplate && (
                <DocumentModal
                    title={
                        editingVersion
                            ? `${editingVersion.name}`
                            : isNameStep
                                ? "Введите название документа"
                                : "Заполните данные документа"
                    }
                    versionId={editingVersion?.id || 0}
                    fields={modalFields}
                    initialValues={modalInitialValues}
                    onSubmit={handleModalSubmit}
                    onCancel={handleModalClose}
                    submitButtonText={submitButtonText}
                    groupName={
                        !isNameStep && currentTemplate.question_groups[currentGroupIndex]?.name
                            ? currentTemplate.question_groups[currentGroupIndex].name
                            : undefined
                    }
                />
            )}
            {isDeleteModalOpen && (
                <ConfirmModal
                    isOpen={isDeleteModalOpen}
                    title="Удаление документа"
                    message="Вы уверены, что хотите удалить документ?"
                    onConfirm={confirmDelete}
                    onCancel={closeDeleteModal}
                />
            )}
        </VersionsPageContainer>
    );
};

export default DocumentVersionsPage;
